import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import {
  DialogTrigger,
  Popover,
  Dialog,
  Modal as RACModal,
  ModalOverlay,
  SearchField,
  Input,
  Button as RACButton,
} from "react-aria-components";
import {
  AppError,
  Button,
  Checkbox,
  EmptyState,
  Loader,
  Pagination,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
  Tag,
  type SortDescriptor,
  type TagTone,
} from "@bfc-software/pallet";
import { TFunction } from "i18next";
import {
  ChevronDown,
  Loader as LoaderIcon,
  Search as SearchIcon,
  Sliders,
  UploadCloud,
  X,
} from "react-feather";
import {
  useTranslation,
  TRANSLATION_NAMESPACE,
} from "@bfc-software/bfc-web-ui/i18n";
import { apiClient } from "@bfc-software/bfc-web-ui/lib/iss-api-client";
import palletOverrides from "@bfc-software/bfc-web-ui/pallet-overrides.module.css";

import styles from "./items.module.css";

/**
 * ISS's default/max page size for /items/list -- see itemsSlotsHandler.go's
 * itemsPageLimit in the analogous nextgen_config admin tool.
 */
const PAGE_SIZE = 50;

/** The three boolean flags the catalog tracks per item, and their tag tone. */
const ATTRIBUTES = [
  {
    key: "catchweight",
    flagParam: "catchweight_flag",
    tone: "warning",
    tagKey: "items.attributes.catchweight",
    filterLabelKey: "items.attributes.catchweightFilterLabel",
  },
  {
    key: "lot",
    flagParam: "lot_tracked_flag",
    tone: "info",
    tagKey: "items.attributes.lot",
    filterLabelKey: "items.attributes.lotFilterLabel",
  },
  {
    key: "expiry",
    flagParam: "expiry_tracked_flag",
    tone: "accent",
    tagKey: "items.attributes.expiry",
    filterLabelKey: "items.attributes.expiryFilterLabel",
  },
] as const satisfies ReadonlyArray<{
  key: string;
  flagParam: "catchweight_flag" | "lot_tracked_flag" | "expiry_tracked_flag";
  tone: TagTone;
  tagKey: string;
  filterLabelKey: string;
}>;
type AttributeKey = (typeof ATTRIBUTES)[number]["key"];

type ItemsSearch = {
  q: string;
  attrs: string;
  offset: number;
};

type ItemRow = {
  item_ext_id: string;
  /**
   * ISS types this as string[], but UPCs/GTINs are effectively bigints and
   * at least one deployment has returned them as bare JSON numbers -- render
   * through formatUpc rather than interpolating raw, or a value like
   * 50707531845142 risks coming out as "5.07075e+13" (scientific notation
   * loses the exact digits, unlike the same value quoted as a string).
   */
  upcs: Array<string | number>;
  item_desc: string;
  catchweight_flag: boolean;
  lot_tracked_flag: boolean;
  expiry_tracked_flag: boolean;
  expected_weight?: number | null;
  weight_uom?: string | null;
  /** Percentage points, e.g. 2.5 means 2.5%. */
  weight_tolerance_min?: number | null;
  weight_tolerance_max?: number | null;
  substitute_item_ids?: string[] | null;
};

type ImportRowError = { row: number; column?: string; message: string };

type ImportOutcome =
  | {
      kind: "success";
      imported: number;
      errorCount: number;
      rowErrors: ImportRowError[];
    }
  | { kind: "failed"; message: string };

function parseAttrs(value: unknown): AttributeKey[] {
  if (typeof value !== "string" || value === "") return [];
  const known = new Set(ATTRIBUTES.map((a) => a.key));
  return Array.from(
    new Set(
      value.split(",").filter((entry) => known.has(entry as AttributeKey)),
    ),
  ) as AttributeKey[];
}

function parseOffset(value: unknown): number {
  const n = Number(value);
  return Number.isSafeInteger(n) && n >= 0 ? n : 0;
}

/**
 * ISS has no combined free-text search across item_ext_id/item_desc/UPC --
 * /items/list only offers an exact item_ext_id match and a substring
 * item_desc match (see ListItems in the generated ISS client). Item IDs in
 * this catalog are digits-hyphen-digit (e.g. "71616-8"), so a query shaped
 * like that is sent as an exact item_ext_id lookup; anything else searches
 * description text. There's no way to search by UPC yet.
 */
const ITEM_ID_SHAPE = /^\d+-\d+$/;

function searchFilterParam(
  q: string,
): { item_ext_id: string } | { item_desc: string } | object {
  const trimmed = q.trim();
  if (!trimmed) return {};
  return ITEM_ID_SHAPE.test(trimmed)
    ? { item_ext_id: trimmed }
    : { item_desc: trimmed };
}

/**
 * Extracts a readable message from whatever a failed ISS mutation rejects
 * with -- openapi-react-query throws the parsed ErrorResponse body itself
 * (`{ error, message }`) rather than an `Error`, so `instanceof Error` alone
 * would miss it.
 */
function errorMessage(error: unknown, fallback: string): string {
  if (error && typeof error === "object") {
    const body = error as { message?: unknown; error?: unknown };
    if (typeof body.message === "string" && body.message) return body.message;
    if (typeof body.error === "string" && body.error) return body.error;
  }
  if (error instanceof Error && error.message) return error.message;
  return fallback;
}

/**
 * UPCs are digit strings, not quantities -- BigInt (not Number) round-trips
 * them exactly however large they get, so a bare JSON number from the API
 * never degrades into something like "5.07075e+13" here.
 */
function formatUpc(upc: string | number): string {
  return typeof upc === "number" ? BigInt(upc).toString() : upc;
}

function formatExpectedWeight(item: ItemRow): string {
  return typeof item.expected_weight === "number"
    ? item.expected_weight.toFixed(2)
    : "—";
}

function formatWeightUnit(item: ItemRow): string {
  return item.weight_uom ?? "—";
}

/**
 * weight_tolerance_min/max are percentages (e.g. 2.5 == 2.5%) applied to
 * expected_weight -- not absolute weights -- so the allowable variance and
 * range have to be derived rather than read off the item directly.
 */
function allowableVariance(item: ItemRow): { min: number; max: number } | null {
  const {
    expected_weight: expectedWeight,
    weight_tolerance_min: toleranceMin,
    weight_tolerance_max: toleranceMax,
  } = item;
  if (
    typeof expectedWeight !== "number" ||
    typeof toleranceMin !== "number" ||
    typeof toleranceMax !== "number"
  ) {
    return null;
  }
  return {
    min: (expectedWeight * toleranceMin) / 100,
    max: (expectedWeight * toleranceMax) / 100,
  };
}

function formatAllowableVariance(item: ItemRow): string {
  const variance = allowableVariance(item);
  if (!variance) return "—";
  return `- ${variance.min.toFixed(2)} / + ${variance.max.toFixed(2)}`;
}

function formatAllowableRange(item: ItemRow): string {
  const variance = allowableVariance(item);
  if (!variance || typeof item.expected_weight !== "number") return "—";
  const min = item.expected_weight - variance.min;
  const max = item.expected_weight + variance.max;
  const uom = item.weight_uom ? ` ${item.weight_uom}` : "";
  return `${min.toFixed(2)} - ${max.toFixed(2)}${uom}`;
}

export const Route = createFileRoute(
  "/$lang/_layout/orgs/$org/_layout/outbound/whs/$location/_layout/items",
)({
  validateSearch: (search?: Record<string, unknown>): ItemsSearch => ({
    q: typeof search?.["q"] === "string" ? search["q"] : "",
    attrs: parseAttrs(search?.["attrs"]).join(","),
    offset: parseOffset(search?.["offset"]),
  }),
  component: RouteComponent,
});

function AttributeTags({ item, t }: { item: ItemRow; t: TFunction }) {
  const flags: Array<{ key: AttributeKey; active: boolean }> = [
    { key: "catchweight", active: item.catchweight_flag },
    { key: "lot", active: item.lot_tracked_flag },
    { key: "expiry", active: item.expiry_tracked_flag },
  ];
  const active = flags.filter((f) => f.active);
  if (active.length === 0) return null;

  return (
    <div className={styles.attributeTags}>
      {active.map(({ key }) => {
        const attribute = ATTRIBUTES.find((a) => a.key === key)!;
        return (
          <Tag
            key={key}
            label={t(attribute.tagKey)}
            display={{ tone: attribute.tone }}
          />
        );
      })}
    </div>
  );
}

function ItemTableRow({
  item,
  t,
  onOpen,
}: {
  item: ItemRow;
  t: TFunction;
  onOpen: (item: ItemRow) => void;
}) {
  const [firstUpc, ...restUpcs] = item.upcs;

  return (
    <TableRow data-test-role="item-row" onAction={() => onOpen(item)}>
      <TableCell>
        <span className={styles.itemExtId}>{item.item_ext_id}</span>
      </TableCell>
      <TableCell>{item.item_desc}</TableCell>
      <TableCell>
        <span className={styles.upc}>
          {firstUpc !== undefined ? formatUpc(firstUpc) : "—"}
        </span>
        {restUpcs.length > 0 ? (
          <span className={styles.upcMore}>
            {t("items.table.moreUpcs", { count: restUpcs.length })}
          </span>
        ) : null}
      </TableCell>
      <TableCell>
        <AttributeTags item={item} t={t} />
      </TableCell>
    </TableRow>
  );
}

function AttributesFilter({
  selected,
  counts,
  onChange,
  onClearAll,
  t,
}: {
  selected: AttributeKey[];
  counts: Record<AttributeKey, number | undefined>;
  onChange: (next: AttributeKey[]) => void;
  onClearAll: () => void;
  t: TFunction;
}) {
  const toggle = (key: AttributeKey, isSelected: boolean) => {
    onChange(
      isSelected ? [...selected, key] : selected.filter((k) => k !== key),
    );
  };

  return (
    <DialogTrigger>
      <RACButton className={styles.toolbarButton}>
        <Sliders size={15} aria-hidden={true} />
        {t("items.filters.attributesLabel")}
        {selected.length > 0 ? (
          <span className={styles.filterCountBadge}>{selected.length}</span>
        ) : null}
        <ChevronDown size={14} aria-hidden={true} />
      </RACButton>
      <Popover className={styles.attributesPopover} placement="bottom end">
        <Dialog className={styles.attributesDialog}>
          <div className={styles.attributesHeading}>
            {t("items.filters.trackedAttributes")}
          </div>
          <ul className={styles.attributesList}>
            {ATTRIBUTES.map((attribute) => (
              <li key={attribute.key} className={styles.attributeRow}>
                <Checkbox
                  name="attributes"
                  value={attribute.key}
                  label={t(attribute.filterLabelKey)}
                  isSelected={selected.includes(attribute.key)}
                  onChange={(isSelected) => toggle(attribute.key, isSelected)}
                />
                <span className={styles.attributeCount}>
                  {counts[attribute.key] ?? "—"}
                </span>
              </li>
            ))}
          </ul>
          <button
            type="button"
            className={styles.clearAllLink}
            onClick={onClearAll}
            disabled={selected.length === 0}
          >
            {t("items.filters.clearAll")}
          </button>
        </Dialog>
      </Popover>
    </DialogTrigger>
  );
}

function ImportModal({
  onImport,
  isPending,
  t,
}: {
  onImport: (file: File) => Promise<void>;
  isPending: boolean;
  t: TFunction;
}) {
  const [file, setFile] = useState<File | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);

  return (
    <DialogTrigger>
      <RACButton className={styles.importTrigger}>
        <UploadCloud size={15} aria-hidden={true} />
        {t("items.import.button")}
      </RACButton>
      <ModalOverlay isDismissable={true} className={styles.modalOverlay}>
        <RACModal className={styles.modal}>
          <Dialog className={styles.dialog}>
            {({ close }) => (
              <>
                <div className={styles.modalHeader}>
                  <strong>{t("items.import.modalTitle")}</strong>
                  <RACButton
                    className={styles.modalDismiss}
                    aria-label={t("items.drawer.close")}
                    onPress={() => {
                      setFile(null);
                      close();
                    }}
                  >
                    <X size={16} aria-hidden={true} />
                  </RACButton>
                </div>

                <label
                  className={styles.dropzone}
                  data-drag-over={isDragOver}
                  onDragOver={(event) => {
                    event.preventDefault();
                    setIsDragOver(true);
                  }}
                  onDragLeave={() => setIsDragOver(false)}
                  onDrop={(event) => {
                    event.preventDefault();
                    setIsDragOver(false);
                    const dropped = event.dataTransfer.files?.[0];
                    if (dropped) setFile(dropped);
                  }}
                >
                  <input
                    type="file"
                    accept=".csv"
                    className={styles.dropzoneInput}
                    onChange={(event) =>
                      setFile(event.target.files?.[0] ?? null)
                    }
                  />
                  <UploadCloud size={28} aria-hidden={true} />
                  <strong>
                    {file ? file.name : t("items.import.chooseFile")}
                  </strong>
                  <span>{t("items.import.dropHint")}</span>
                </label>

                <div className={styles.modalFooter}>
                  <Button
                    variant="secondary"
                    text={t("items.import.cancel")}
                    isDisabled={isPending}
                    onPress={() => {
                      setFile(null);
                      close();
                    }}
                  />
                  <span className={styles.uploadAction}>
                    {isPending ? (
                      <LoaderIcon
                        size={15}
                        className={styles.uploadSpinner}
                        aria-hidden={true}
                      />
                    ) : null}
                    <Button
                      className={styles.uploadButton}
                      text={
                        isPending
                          ? t("items.import.importing")
                          : t("items.import.upload")
                      }
                      isDisabled={!file || isPending}
                      onPress={() => {
                        if (!file) return;
                        onImport(file).then(() => {
                          setFile(null);
                          close();
                        });
                      }}
                    />
                  </span>
                </div>
              </>
            )}
          </Dialog>
        </RACModal>
      </ModalOverlay>
    </DialogTrigger>
  );
}

function ItemDrawer({
  item,
  onClose,
  t,
}: {
  item: ItemRow;
  onClose: () => void;
  t: TFunction;
}) {
  return (
    <div
      className={styles.drawerBackdrop}
      onClick={onClose}
      role="presentation"
    >
      <div
        className={styles.drawer}
        role="dialog"
        aria-label={item.item_ext_id}
        onClick={(event) => event.stopPropagation()}
      >
        <div className={styles.drawerHeader}>
          <div>
            <h2>{item.item_ext_id}</h2>
            <span>{item.item_desc}</span>
          </div>
          <button
            type="button"
            className={styles.drawerClose}
            aria-label={t("items.drawer.close")}
            onClick={onClose}
          >
            <X size={18} aria-hidden={true} />
          </button>
        </div>

        <div className={styles.drawerSection}>
          <div className={styles.drawerSectionHeading}>
            {t("items.drawer.catalogAttributes")}
          </div>
          <dl className={styles.drawerGrid}>
            <div>
              <dt>{t("items.table.itemExtId")}</dt>
              <dd>{item.item_ext_id}</dd>
            </div>
            <div>
              <dt>{t("items.table.description")}</dt>
              <dd>{item.item_desc}</dd>
            </div>
            <div>
              <dt>{t(ATTRIBUTES[1].filterLabelKey)}</dt>
              <dd>
                {item.lot_tracked_flag
                  ? t("items.filters.yes")
                  : t("items.filters.no")}
              </dd>
            </div>
            <div>
              <dt>{t(ATTRIBUTES[2].filterLabelKey)}</dt>
              <dd>
                {item.expiry_tracked_flag
                  ? t("items.filters.yes")
                  : t("items.filters.no")}
              </dd>
            </div>
          </dl>

          <div className={styles.catchweightBox}>
            <div className={styles.catchweightHeader}>
              <span>{t("items.attributes.catchweight")}</span>
              <Tag
                label={
                  item.catchweight_flag
                    ? t("items.filters.yes")
                    : t("items.filters.no")
                }
                display={{
                  tone: item.catchweight_flag ? "success" : "neutral",
                }}
              />
            </div>
            {item.catchweight_flag ? (
              <dl className={styles.catchweightGrid}>
                <div>
                  <dt>{t("items.drawer.expectedWeight")}</dt>
                  <dd>{formatExpectedWeight(item)}</dd>
                </div>
                <div>
                  <dt>{t("items.drawer.weightUnit")}</dt>
                  <dd>{formatWeightUnit(item)}</dd>
                </div>
                <div>
                  <dt>
                    {t("items.drawer.allowableVariance")}{" "}
                    <span className={styles.dtSuffix}>
                      {t("items.drawer.minMaxSuffix")}
                    </span>
                  </dt>
                  <dd>{formatAllowableVariance(item)}</dd>
                </div>
                <div>
                  <dt>
                    {t("items.drawer.allowableRange")}{" "}
                    <span className={styles.dtSuffix}>
                      {t("items.drawer.calculatedSuffix")}
                    </span>
                  </dt>
                  <dd>{formatAllowableRange(item)}</dd>
                </div>
              </dl>
            ) : null}
          </div>
        </div>

        <div className={styles.drawerSection}>
          <div className={styles.drawerSectionHeading}>
            {t("items.drawer.upcs")}
          </div>
          <div className={styles.pillList}>
            {item.upcs.map((upc) => (
              <span key={upc} className={styles.pill}>
                {formatUpc(upc)}
              </span>
            ))}
          </div>
        </div>

        <div className={styles.drawerSection}>
          <div className={styles.drawerSectionHeading}>
            {t("items.drawer.substitutableItems")}
          </div>
          {item.substitute_item_ids && item.substitute_item_ids.length > 0 ? (
            <div className={styles.pillList}>
              {item.substitute_item_ids.map((id) => (
                <span key={id} className={styles.pillAccent}>
                  {id}
                </span>
              ))}
            </div>
          ) : null}
        </div>

        <div className={styles.drawerFooter}>
          <span>{t("items.drawer.readOnlyNotice")}</span>
          <Button
            variant="secondary"
            text={t("items.drawer.edit")}
            isDisabled={true}
          />
        </div>
      </div>
    </div>
  );
}

function ImportBanner({
  outcome,
  onDismiss,
  t,
}: {
  outcome: ImportOutcome;
  onDismiss: () => void;
  t: TFunction;
}) {
  if (outcome.kind === "failed") {
    return (
      <div className={styles.importBanner} data-tone="danger">
        <span>{t("items.import.failed", { message: outcome.message })}</span>
        <button
          type="button"
          className={styles.bannerDismiss}
          onClick={onDismiss}
        >
          {t("items.import.dismiss")}
        </button>
      </div>
    );
  }

  return (
    <div
      className={styles.importBanner}
      data-tone={outcome.errorCount > 0 ? "warning" : "success"}
    >
      <div className={styles.importBannerSummary}>
        <span>
          {t("items.import.successCount", { count: outcome.imported })}
        </span>
        {outcome.errorCount > 0 ? (
          <span>
            {t("items.import.errorCount", { count: outcome.errorCount })}
          </span>
        ) : null}
        <button
          type="button"
          className={styles.bannerDismiss}
          onClick={onDismiss}
        >
          {t("items.import.dismiss")}
        </button>
      </div>
      {outcome.rowErrors.length > 0 ? (
        <div className={styles.importErrors}>
          <strong>{t("items.import.errorsHeading")}</strong>
          <ul>
            {outcome.rowErrors.map((rowError, index) => (
              <li key={`${rowError.row}-${rowError.column ?? index}`}>
                {t("items.import.rowError", {
                  row: rowError.row,
                  message: rowError.column
                    ? `${rowError.column}: ${rowError.message}`
                    : rowError.message,
                })}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

export function RouteComponent() {
  const params = Route.useParams();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const { t } = useTranslation({
    lang: params.lang,
    namespace: TRANSLATION_NAMESPACE.outbound,
  });

  const client = useMemo(() => apiClient(), []);
  const [importOutcome, setImportOutcome] = useState<ImportOutcome | null>(
    null,
  );
  const [openItem, setOpenItem] = useState<ItemRow | null>(null);
  /**
   * ISS's ListItems has no sort param -- it returns a fixed order (by
   * item_ext_id). This only re-orders the page already in hand; it does not
   * ask the server for a different page 2+ order.
   */
  const [sortDir, setSortDir] = useState<"ascending" | "descending">(
    "ascending",
  );

  const selectedAttrs = parseAttrs(search.attrs);
  const searchFilter = searchFilterParam(search.q);

  const goTo = (next: Partial<ItemsSearch>) => {
    navigate({
      search: (prev: ItemsSearch) => ({ ...prev, offset: 0, ...next }),
      resetScroll: false,
    });
  };

  const attrQueryParams = Object.fromEntries(
    selectedAttrs.map((key) => [
      ATTRIBUTES.find((a) => a.key === key)!.flagParam,
      true,
    ]),
  );

  const query = client.useQuery(
    "get",
    "/items/list",
    {
      params: {
        query: {
          limit: PAGE_SIZE,
          offset: search.offset,
          ...searchFilter,
          ...attrQueryParams,
        },
      },
    },
    { placeholderData: (previousData) => previousData },
  );

  /** One low-cost (limit: 1) count query per attribute, for the filter menu. */
  const countQueries = ATTRIBUTES.map((attribute) =>
    client.useQuery(
      "get",
      "/items/list",
      {
        params: {
          query: {
            limit: 1,
            offset: 0,
            ...searchFilter,
            [attribute.flagParam]: true,
          },
        },
      },
      { placeholderData: (previousData) => previousData },
    ),
  );
  const attributeCounts = Object.fromEntries(
    ATTRIBUTES.map((attribute, index) => [
      attribute.key,
      countQueries[index].data?.pagination.total_count,
    ]),
  ) as Record<AttributeKey, number | undefined>;

  const importMutation = client.useMutation("post", "/items/import");

  const items = (query.data?.data ?? []) as ItemRow[];
  const sortedItems = [...items].sort((a, b) =>
    sortDir === "ascending"
      ? a.item_ext_id.localeCompare(b.item_ext_id)
      : b.item_ext_id.localeCompare(a.item_ext_id),
  );
  const pagination = query.data?.pagination;
  const total = pagination?.total_count ?? 0;
  const currentPage = Math.floor(search.offset / PAGE_SIZE) + 1;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const rangeStart = total > 0 ? search.offset + 1 : 0;
  const rangeEnd = total > 0 ? search.offset + items.length : 0;

  const sortDescriptor: SortDescriptor = {
    column: "item_ext_id",
    direction: sortDir,
  };

  const handleImport = (file: File) => {
    const formData = new FormData();
    formData.append("file", file);

    return importMutation
      .mutateAsync({ body: formData as unknown as { file: string } })
      .then((response) => {
        setImportOutcome({
          kind: "success",
          imported: response.imported_count,
          errorCount: response.error_count,
          rowErrors: response.errors ?? [],
        });
        query.refetch();
      })
      .catch((error: unknown) => {
        setImportOutcome({
          kind: "failed",
          message: errorMessage(error, t("items.import.dismiss")),
        });
      });
  };

  return (
    <div className={palletOverrides.basicPageContent}>
      <div className={styles.page}>
        <header className={styles.pageHeader}>
          <div className={styles.titleRow}>
            <h1>{t("items.heading")}</h1>
            <div className={styles.toolbar}>
              <SearchField
                aria-label={t("items.filters.searchAriaLabel")}
                className={styles.searchField}
                value={search.q}
                onChange={(value) => goTo({ q: value })}
              >
                <SearchIcon
                  size={15}
                  className={styles.searchIcon}
                  aria-hidden={true}
                />
                <Input placeholder={t("items.filters.searchPlaceholder")} />
                {search.q ? (
                  <RACButton
                    className={styles.searchClear}
                    onPress={() => goTo({ q: "" })}
                  >
                    <X size={13} aria-hidden={true} />
                  </RACButton>
                ) : null}
              </SearchField>

              <AttributesFilter
                selected={selectedAttrs}
                counts={attributeCounts}
                onChange={(next) => goTo({ attrs: next.join(",") })}
                onClearAll={() => goTo({ attrs: "" })}
                t={t}
              />

              <ImportModal
                onImport={handleImport}
                isPending={importMutation.isPending}
                t={t}
              />
            </div>
          </div>
          <p>{t("items.subheading")}</p>
        </header>

        {importOutcome ? (
          <ImportBanner
            outcome={importOutcome}
            onDismiss={() => setImportOutcome(null)}
            t={t}
          />
        ) : null}

        <div className={styles.tableWrap}>
          {query.isLoading ? (
            <Loader type="page" />
          ) : query.isError ? (
            <AppError
              description={t("items.failed")}
              resolution={t("items.failureSupport")}
            />
          ) : (
            <Table
              aria-label={t("items.table.ariaLabel")}
              isResizable={false}
              sortDescriptor={sortDescriptor}
              onSortChange={(descriptor) =>
                setSortDir(
                  descriptor.direction === "descending"
                    ? "descending"
                    : "ascending",
                )
              }
            >
              <TableHead>
                <TableHeaderCell
                  id="item_ext_id"
                  isRowHeader={true}
                  isSortable={true}
                >
                  {t("items.table.itemExtId")}
                </TableHeaderCell>
                <TableHeaderCell>
                  {t("items.table.description")}
                </TableHeaderCell>
                <TableHeaderCell>{t("items.table.upc")}</TableHeaderCell>
                <TableHeaderCell>{t("items.table.attributes")}</TableHeaderCell>
              </TableHead>
              <TableBody>
                {sortedItems.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4}>
                      <EmptyState heading={t("items.empty")} />
                    </TableCell>
                  </TableRow>
                ) : (
                  sortedItems.map((item) => (
                    <ItemTableRow
                      key={item.item_ext_id}
                      item={item}
                      t={t}
                      onOpen={setOpenItem}
                    />
                  ))
                )}
              </TableBody>
            </Table>
          )}
        </div>

        {!query.isLoading && !query.isError && total > 0 ? (
          <div className={styles.tableFooter}>
            <span className={styles.rangeSummary}>
              {t("items.rangeSummary", {
                start: rangeStart,
                end: rangeEnd,
                total,
              })}
            </span>
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              className={palletOverrides.basicPagePagination}
              nextButton={{
                isDisabled: search.offset + items.length >= total,
                onPress: () =>
                  navigate({
                    search: (prev: ItemsSearch) => ({
                      ...prev,
                      offset: prev.offset + PAGE_SIZE,
                    }),
                  }),
              }}
              previousButton={{
                isDisabled: search.offset <= 0,
                onPress: () =>
                  navigate({
                    search: (prev: ItemsSearch) => ({
                      ...prev,
                      offset: Math.max(0, prev.offset - PAGE_SIZE),
                    }),
                  }),
              }}
              jumpTo={(page) =>
                navigate({
                  search: (prev: ItemsSearch) => ({
                    ...prev,
                    offset: Math.max(0, (page - 1) * PAGE_SIZE),
                  }),
                })
              }
            />
          </div>
        ) : null}
      </div>

      {openItem ? (
        <ItemDrawer item={openItem} onClose={() => setOpenItem(null)} t={t} />
      ) : null}
    </div>
  );
}
